import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzInputModule } from 'ng-zorro-antd/input';
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { ApiService, CartStateService, SessionService } from '../../services';
import { NzModalService } from 'ng-zorro-antd/modal';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  CartItem,
  EventModel,
  initiatePaymentReq,
  Order,
  OrderStatus,
  PaymentMethod,
  TransactionStatus,
} from '../../models';
import { OrderFee } from '../../models/order-fees';
import { StoreOrderDetails } from '../../models/store-order-details';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { interval, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { isSeatHoldExpired, seatHoldExpiredMessage } from '@hypenation/shared';
import {
  formatCountdown,
  SeatHoldDeadline,
  seatHoldDeadline,
  secondsUntil,
  seatUidsOf,
} from '../../utils/seat-hold-deadline';

@Component({
  selector: 'hypenation-tickets-checkout-page',
  standalone: true,
  imports: [
    CommonModule,
    NzImageModule,
    NzNotificationModule,
    NzInputModule,
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzCardModule,
    NzButtonModule,
    NzSkeletonModule,
    NzStepsModule,
  ],
  templateUrl: './checkout-page.component.html',
  styleUrl: './checkout-page.component.css',
  providers: [NzModalService],
})
export class CheckoutPageComponent implements OnInit, OnDestroy {
  /** the countdown turns to a warning with this long left */
  static readonly SEAT_HOLD_WARNING_SECONDS = 120;

  checkoutForm: FormGroup;
  checkoutLoading = false;
  cancelling = false;
  /**
   * Seconds the seats of this order stay held, counted down on the page. Null for an order without seats,
   * and when the holds could not be read: the backend still refuses a payment whose seats have lapsed.
   */
  seatSecondsLeft: number | null = null;
  private holdTimer?: Subscription;
  private holdsRequested = false;
  /** the refusal is open (or was): never open a second one for the same lapse */
  private refusing = false;
  orderNumber!: string;
  order!: Order | null;
  event!: EventModel | null;
  loadingFees = false;
  loadingEventFees = false;
  fees: OrderFee[] = [];
  orderDetails!: StoreOrderDetails;

  constructor(
    private readonly _apiService: ApiService,
    private readonly _sessionService: SessionService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly _notificationService: NzNotificationService,
    private readonly _fb: FormBuilder,
    private readonly _cartStateService: CartStateService,
    private readonly _modal: NzModalService
  ) {
    this.checkoutForm = this._fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    this.orderNumber = this._activatedRoute.snapshot.params['orderNumber'];
    const orderDetails = this._sessionService.getOrderDetails();

    if (orderDetails?.order?.orderNumber === this.orderNumber) {
      this.orderDetails = orderDetails;
    }

    this.loadCheckoutDetails(this.orderNumber);

    this.scrollToTop();
  }

  private loadCheckoutDetails(orderNum: string): void {
    this._apiService.getOrderByOrderNumber(orderNum).subscribe({
      next: (res: HttpResponse<Order>) => {
        if (res.body) {
          if (this.orderDetails) {
            this.orderDetails = { ...this.orderDetails, order: res.body };
          } else {
            this.orderDetails = { order: res.body, orderItems: [] };
          }
          this.startSeatHoldCountdown();
        }
      },
    });

    this._apiService.getItemsByOrderNumber(orderNum).subscribe({
      next: (res: HttpResponse<any[]>) => {
        const items = (res.body ?? []) as CartItem[];
        if (this.orderDetails) {
          this.orderDetails = { ...this.orderDetails, orderItems: items };
          this.startSeatHoldCountdown();
        }
      },
    });

    this.getOrderFees(orderNum);
  }

  public scrollToTop(): void {
    this._apiService.scrollToTop();
  }

  public getOrderFees(orderNum: string): void {
    this.loadingFees = true;

    this._apiService.getOrderFeesByOrderNumber(orderNum).subscribe({
      next: (res: HttpResponse<OrderFee[]>) => {
        if (res.body) {
          this.fees = res.body;
        }
        this.loadingFees = false;
      },

      error: () => {
        this.loadingFees = false;
      },
      complete: () => {
        this.loadingFees = false;
      },
    });
  }

  public getEventFees(eventuid: string): void {
    this.loadingEventFees = true;

    this._apiService.getEventByUid(eventuid).subscribe({
      next: (res: HttpResponse<EventModel>) => {
        if (res.body) this.event = res.body;
        this.loadingEventFees = false;
      },

      error: () => {
        this.loadingEventFees = false;
      },
      complete: () => {
        this.loadingEventFees = false;
      },
    });
  }

  public getItemsTotal(): number {
    if (this.orderDetails && this.orderDetails.orderItems) {
      return this.orderDetails.orderItems.reduce(
        (acc, item) => acc + item.amount,
        0
      );
    }
    return 0;
  }

  public addOnsText(item: CartItem): string {
    return String(item.purchaseDetails?.['addOnsText'] ?? '');
  }

  public hasSelectedAddOns(item: CartItem): boolean {
    return !!this.addOnsText(item);
  }

  public getFeesTotal(): number {
    if (this.fees && this.fees.length > 0) {
      return this.fees.reduce((acc, fee) => {
        const amount = fee.amount || fee.fee?.amount || 0;
        return acc + amount;
      }, 0);
    }
    return 0;
  }

  public getBreakdownSubtotal(): number {
    return this.getItemsTotal() + this.getFeesTotal() + this.getDeliveryFee();
  }

  /**
   * What the buyer is charged. The backend keeps the processing fee out of order.amount (tickets + delivery)
   * and adds it when it starts the payment, so the total here is amount + processingFee.
   */
  private getBackendTotal(): number | null {
    const order = this.orderDetails?.order;
    if (typeof order?.amount !== 'number') return null;
    return order.amount + (order.processingFee || 0);
  }

  public getAdjustmentAmount(): number {
    const charged = this.getBackendTotal();
    if (charged === null) return 0;
    return charged - this.getBreakdownSubtotal();
  }

  public shouldShowAdjustment(): boolean {
    return Math.abs(this.getAdjustmentAmount()) >= 0.01;
  }

  public calculateTotal(): number {
    const charged = this.getBackendTotal();
    if (charged !== null) return charged;

    const itemsTotal = this.getItemsTotal();
    const feesTotal = this.getFeesTotal();
    return itemsTotal + feesTotal + this.getDeliveryFee();
  }

  public getCurrencyCode(): string {
    return this.orderDetails?.order?.currencyCode || 'USD';
  }

  public getDeliveryFee(): number {
    const order = this.orderDetails?.order as Order & {
      deliveryFee?: number;
      deliveryAmount?: number;
    };

    if (!order) return 0;
    if (typeof order.deliveryFee === 'number') return order.deliveryFee;
    if (typeof order.deliveryAmount === 'number') return order.deliveryAmount;
    return 0;
  }

  public shouldShowDeliveryFee(): boolean {
    const order = this.orderDetails?.order as
      | (Order & {
          deliveryFee?: number;
          deliveryAmount?: number;
        })
      | undefined;
    if (!order) return false;
    // no line when delivery is not in use: a $0.00 delivery fee is the same as none
    return this.getDeliveryFee() > 0;
  }

  public submit(): void {
    if (this.checkoutForm.valid) {
      // Submit
    } else {
      Object.values(this.checkoutForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  public initiatePaymentWithOrderNumber(): void {
    const orderNumber = this.orderDetails?.order.orderNumber;
    const paymentReq: initiatePaymentReq = {
      returnUrl: `${window.location.origin}/order-confirmation/${this.orderDetails.order.orderNumber}`,
      paymentMethod: PaymentMethod.ONLINE,
    };

    this.checkoutLoading = true;
    if (orderNumber)
      this._apiService.initiatePayment(paymentReq, orderNumber).subscribe({
        next: (res: HttpResponse<Order>) => {
          this.checkoutLoading = false;
          if (res.body) {
            this._apiService.getOrdersByOrderNumber(orderNumber).subscribe({
              next: (res: HttpResponse<Order>) => {
                if (res.body) {
                  // check order status
                  if (
                    res.body.paymentTransaction?.transactionStatus ===
                      TransactionStatus.SUCCESS ||
                    res.body.status === OrderStatus.COMPLETED ||
                    res.body.status === OrderStatus.IN_PROGRESS
                  ) {
                    this.router.navigateByUrl(
                      `/order-confirmation/${this.orderDetails.order.orderNumber}`
                    );
                  } else {
                    window.location.href =
                      res.body.paymentTransaction.redirectUrl;
                  }
                }
              },
            });
          }
        },
        error: (err: HttpErrorResponse) => {
          this.checkoutLoading = false;
          if (isSeatHoldExpired(err)) {
            this.refuseLapsedSeats(err);
          }
        },
        complete: () => {
          this.checkoutLoading = false;
        },
      });
  }

  /** "11:05": what is left of the hold on the seats of this order */
  get seatCountdownText(): string {
    return formatCountdown(this.seatSecondsLeft ?? 0);
  }

  /** little time left: the countdown is shown as a warning */
  get seatHoldLow(): boolean {
    return this.seatSecondsLeft !== null && this.seatSecondsLeft <= CheckoutPageComponent.SEAT_HOLD_WARNING_SECONDS;
  }

  /**
   * Counts down the hold on the seats of this order, once the order and its items are both known and the
   * order can still be paid. Seats the cart has already lost are refused at once, and so is a countdown that
   * reaches zero: the backend would refuse the payment either way, and this says so before the buyer tries.
   * Orders without seats have no countdown.
   */
  public startSeatHoldCountdown(): void {
    const order = this.orderDetails?.order;
    const items = this.orderDetails?.orderItems ?? [];
    if (this.holdsRequested || !order?.cartUid || items.length === 0 || !this.canCancelOrder) {
      return;
    }
    const seatUids = seatUidsOf(items);
    if (seatUids.length === 0) {
      return;
    }
    this.holdsRequested = true;
    this._apiService.getSeatHolds(order.cartUid).subscribe({
      next: (holds) => this.applySeatHolds(seatHoldDeadline(seatUids, holds, Date.now())),
      // no countdown rather than a wrong one: the backend still refuses a payment whose seats have lapsed
      error: () => undefined,
    });
  }

  private applySeatHolds(deadline: SeatHoldDeadline): void {
    if (deadline.kind === 'lost') {
      this.refuseLapsedSeats();
      return;
    }
    if (deadline.kind !== 'held') {
      return;
    }
    const tick = (): void => {
      this.seatSecondsLeft = secondsUntil(deadline.endsAt, Date.now());
      if (this.seatSecondsLeft === 0) {
        this.refuseLapsedSeats();
      }
    };
    tick();
    if (this.seatSecondsLeft !== 0) {
      this.holdTimer = interval(1000).subscribe(tick);
    }
  }

  private stopHoldTimer(): void {
    this.holdTimer?.unsubscribe();
    this.holdTimer = undefined;
  }

  ngOnDestroy(): void {
    this.stopHoldTimer();
  }

  /**
   * The backend refused to start payment because a seat of this order is no longer held: the hold ran out and
   * the seat may already be someone else's. Paying is not possible and will not become possible, so say so
   * plainly, take no further action on the payment, and leave only one way forward: choose seats again.
   */
  private refuseLapsedSeats(error?: HttpErrorResponse): void {
    if (this.refusing) {
      return;
    }
    this.refusing = true;
    this.stopHoldTimer();
    this._modal.error({
      nzTitle: 'Your seat reservation has expired',
      nzContent: `${seatHoldExpiredMessage(error ?? null)} No payment was taken.`,
      nzOkText: 'Choose seats again',
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzOnOk: () => this.releaseLapsedOrder(),
    });
  }

  /**
   * Gives up the order that can no longer be paid, empties the cart of its stale seats (left in the cart they
   * would answer "already in your cart" when the same seat is picked again), and sends the buyer back to pick.
   * Whatever fails on the way, the buyer still leaves: the order cannot be paid either way.
   */
  private releaseLapsedOrder(): void {
    const order = this.orderDetails?.order;
    const leave = (): void => {
      this._sessionService.clearPendingOrder();
      this._cartStateService.notifyCartUpdate();
      this.router.navigateByUrl('/');
    };
    if (!order?.orderNumber) {
      leave();
      return;
    }
    this._apiService
      .cancelOrder(order.orderNumber, order.cartUid)
      .pipe(switchMap(() => (order.cartUid ? this._apiService.clearCart(order.cartUid) : of(undefined))))
      .subscribe({
        next: leave,
        error: (err: HttpErrorResponse) => {
          if (err.status === 409) {
            // A payment is already in flight or done for this order: the confirmation page has the truth.
            this.router.navigateByUrl(`/order-confirmation/${order.orderNumber}`);
          } else {
            leave();
          }
        },
      });
  }

  /** The order can be cancelled (and the customer sent back to edit their cart) only while it is
   * pending and unpaid. A completed/closed/cancelled order has no back-to-cart path. */
  get canCancelOrder(): boolean {
    const status = this.orderDetails?.order?.status;
    return status === OrderStatus.INITIATED || status === OrderStatus.RESERVED;
  }

  /** Confirm, then cancel the pending order and return the customer to their (still-populated) cart. */
  public backToCart(): void {
    const order = this.orderDetails?.order;
    if (!order) {
      return;
    }
    this._modal.confirm({
      nzTitle: 'Return to cart',
      nzContent: 'Cancel this order and go back to your cart to edit your items?',
      nzOkText: 'Yes, edit cart',
      nzCancelText: 'Keep order',
      nzOnOk: () => this.cancelAndReturn(order.orderNumber, order.cartUid),
    });
  }

  private cancelAndReturn(orderNumber: string, cartUid: string): void {
    this.cancelling = true;
    this._apiService.cancelOrder(orderNumber, cartUid).subscribe({
      next: () => {
        // Drop the pending-order pointer so the navbar stops resuming the dead order, then refresh
        // the cart so the badge reflects the restored items.
        this._sessionService.clearPendingOrder();
        this._cartStateService.notifyCartUpdate();
        this.cancelling = false;
        this.router.navigateByUrl(cartUid ? `/cart/${cartUid}` : '/');
      },
      error: (err: HttpErrorResponse) => {
        this.cancelling = false;
        if (err.status === 409) {
          // Already paid/completed or a payment is in flight — cannot cancel. Leave local state
          // intact and send the customer to the confirmation page for the order's real state.
          this._notificationService.info(
            'Order can no longer be cancelled',
            'This order is already being processed. Redirecting you to your order.'
          );
          this.router.navigateByUrl(`/order-confirmation/${orderNumber}`);
        } else {
          this._notificationService.error(
            'Could not cancel',
            'Something went wrong cancelling your order. Please try again.'
          );
        }
      },
    });
  }
}
